import { X } from "lucide-react";
import { formatCurrency, getUsdRate } from "@vaulzo/core/constants";

const CURRENCIES = ["INR", "USD", "EUR"];

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for bank accounts: one row per account with its native
 * currency. Non-INR balances convert at today's rate and are kept current
 * by the nightly FX revaluation.
 */
export default function BankHoldingsEditor({ holdings, setHoldings, exchangeRates }) {
  const rateFor = (ccy) => {
    if (ccy === "INR") return 1;
    if (ccy === "USD") return getUsdRate(exchangeRates);
    return exchangeRates?.[ccy] || 0;
  };

  const patchRow = (i, patch) =>
    setHoldings((rows) => rows.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings((rows) => rows.filter((_, idx) => idx !== i));
  const addRow = () =>
    setHoldings((rows) => [...rows, { label: "", currency: "INR", balance: "", buyRate: "" }]);

  const total = holdings.reduce((s, h) => {
    const bal = parseFloat(h.balance);
    const rate = rateFor(h.currency);
    return bal > 0 && rate > 0 ? s + bal * rate : s;
  }, 0);

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Accounts</span>
        <span className="text-[12px] text-vaulzo-fainter">Foreign balances follow the daily FX rate</span>
      </div>

      {holdings.map((h, i) => {
        const bal = parseFloat(h.balance);
        const rate = rateFor(h.currency);
        const inr = bal > 0 && rate > 0 ? bal * rate : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-3 first:pt-1 last:border-b-0">
            <div className="flex items-center gap-3">
              <input
                value={h.label}
                onChange={(e) => patchRow(i, { label: e.target.value })}
                placeholder="e.g. HDFC Salary A/c"
                required
                className={`min-w-0 flex-1 text-[14px] ${underline}`}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove account"
                className="flex w-5 flex-none justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            <div className="mt-2 flex gap-4">
              <label className="w-[90px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Currency
                </span>
                <select
                  value={h.currency}
                  onChange={(e) => patchRow(i, { currency: e.target.value })}
                  className={`w-full cursor-pointer text-[15px] font-semibold ${underline}`}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Balance
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  required
                  value={h.balance}
                  onChange={(e) => patchRow(i, { balance: e.target.value })}
                  placeholder="0"
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
            </div>

            {h.currency !== "INR" && (
              <label className="mt-3 block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Bought at (₹ per {h.currency}) · optional
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  value={h.buyRate ?? ""}
                  onChange={(e) => patchRow(i, { buyRate: e.target.value })}
                  placeholder={rate > 0 ? rate.toFixed(2) : "0"}
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
            )}

            {inr != null && h.currency !== "INR" && (
              <div className="num pt-2 text-[12px] text-vaulzo-fainter">
                ≈ {formatCurrency(inr)} at ₹{rate.toFixed(2)}/{h.currency}
                {parseFloat(h.buyRate) > 0 && (() => {
                  const change = bal * (rate - parseFloat(h.buyRate));
                  return (
                    <span className={change >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                      {" "}· {change >= 0 ? "+" : "−"}
                      {formatCurrency(Math.abs(change))} currency change
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add account
      </button>

      {total > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total <span className="font-semibold text-vaulzo-ink">{formatCurrency(total)}</span>
        </div>
      )}
    </div>
  );
}
